import { GridApi, Column, IRowNode } from 'ag-grid-community';

interface AutoSizeOptions {
	skipHeader?: boolean;
	saveStateCallback?: () => void;
	paddingAdjustment?: number;
}

export const autoSizeAllColumns = (api: GridApi<any>, options: AutoSizeOptions = {}) => {
	const { skipHeader = false, saveStateCallback, paddingAdjustment = -15 } = options;
	const cols: Column[] = api.getColumns() ?? [];
	if (!cols.length) return;

	// 1단계: AG-Grid의 기본 너비 맞추기 기능을 실행합니다. (데이터 행 기준)
	api.autoSizeColumns(cols, skipHeader);

	// Pinned Bottom Rows의 데이터를 올바른 API를 사용해 가져옵니다.
	const pinnedBottomRowNodes: IRowNode[] = [];
	const pinnedBottomRowCount = api.getPinnedBottomRowCount();
	for (let i = 0; i < pinnedBottomRowCount; i++) {
		const rowNode = api.getPinnedBottomRow(i);
		if (rowNode) {
			pinnedBottomRowNodes.push(rowNode);
		}
	}

	const requiredWidths = new Map<string, number>();

	// 현재 자동계산된 너비를 초기값으로 설정합니다.
	cols.forEach(col => {
		requiredWidths.set(col.getColId(), col.getActualWidth());
	});

	// 2단계: 고정 행이 있다면, 해당 행의 너비를 수동으로 계산합니다.
	if (pinnedBottomRowNodes.length > 0) {
		const ruler = document.createElement("span");
		document.body.appendChild(ruler);
		ruler.style.visibility = 'hidden';
		ruler.style.position = 'absolute';
		ruler.style.whiteSpace = 'nowrap';
		// AG-Grid의 기본 셀 스타일과 유사하게 설정하여 측정 정확도를 높입니다.
		ruler.style.fontFamily = 'var(--ag-font-family, "Malgun Gothic")';
		ruler.style.fontSize = 'var(--ag-font-size, 14px)';

		pinnedBottomRowNodes.forEach(pinnedRowNode => {
			cols.forEach(col => {
				const colId = col.getColId();
				// getCellValue를 사용해 valueGetter/valueFormatter가 적용된 최종 값을 가져옵니다.
				const value = api.getCellValue({
					rowNode: pinnedRowNode,
					colKey: colId,
					useFormatter: true
				});

				if (value !== undefined && value !== null) {
					ruler.textContent = String(value);
					// 텍스트 너비 + 좌우 여백(padding) 20px
					const pinnedCellWidth = ruler.offsetWidth + 20;
					const currentWidth = requiredWidths.get(colId) || 0;

					if (pinnedCellWidth > currentWidth) {
						requiredWidths.set(colId, pinnedCellWidth);
					}
				}
			});
		});

		document.body.removeChild(ruler);
	}

	// 3단계: 최종적으로 계산된 너비에 사용자 조정값을 적용하여 컬럼에 설정합니다.
	const adjustedWidths = Array.from(requiredWidths.entries()).map(([key, newWidth]) => ({
		key,
		newWidth: Math.max(50, newWidth + paddingAdjustment),
	}));

	api.setColumnWidths(adjustedWidths);

	// 4단계: 변경된 상태를 저장합니다.
	if (saveStateCallback) {
		saveStateCallback();
	}
};